/**
 * HslStringVariant - Variante HSL String para Color
 *
 * Representa cor como string hsl()/hsla()
 *
 * @example
 * "hsl(0, 100%, 50%)"
 * "hsla(0, 100%, 50%, 0.5)"
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { RGBA, ColorHslString, ColorFormatOptions } from "../types";
import {
  NAMED_COLORS,
  normalizeAlpha,
  rgbToHsl,
  hslToRgb,
  clamp,
} from "../types";

// Regex patterns
const HSL_REGEX = /^hsl\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*\)$/i;
const HSLA_REGEX =
  /^hsla\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*,\s*([\d.]+)\s*\)$/i;

export class HslStringVariant extends BaseVariant<ColorHslString, RGBA> {
  readonly name = "hsl_string";
  readonly type = "string" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: ColorHslString): RGBA {
    const str = value.trim();

    // Try hsla() first
    let match = str.match(HSLA_REGEX);
    if (match) {
      const h = clamp(parseInt(match[1], 10), 0, 360);
      const s = clamp(parseInt(match[2], 10), 0, 100);
      const l = clamp(parseInt(match[3], 10), 0, 100);
      const a = normalizeAlpha(parseFloat(match[4]));

      const rgb = hslToRgb(h, s, l);
      return { ...rgb, a };
    }

    // Try hsl()
    match = str.match(HSL_REGEX);
    if (match) {
      const h = clamp(parseInt(match[1], 10), 0, 360);
      const s = clamp(parseInt(match[2], 10), 0, 100);
      const l = clamp(parseInt(match[3], 10), 0, 100);

      const rgb = hslToRgb(h, s, l);
      return { ...rgb, a: 1 };
    }

    throw new Error(`Invalid hsl string: ${value}`);
  }

  fromBase(base: RGBA): ColorHslString {
    const hsl = rgbToHsl(base.r, base.g, base.b);
    const a = normalizeAlpha(base.a);

    if (a < 1) {
      return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${a})`;
    }

    return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): ColorHslString | null {
    // Already a string
    if (typeof input === "string") {
      const trimmed = input.trim().toLowerCase();

      // Named color
      if (NAMED_COLORS[trimmed]) {
        return this.fromBase(NAMED_COLORS[trimmed]);
      }

      // Already hsl/hsla format
      if (HSL_REGEX.test(trimmed) || HSLA_REGEX.test(trimmed)) {
        try {
          const rgba = this.toBase(trimmed);
          return this.fromBase(rgba);
        } catch {
          return null;
        }
      }

      // Hex format
      const hexMatch = trimmed.match(
        /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i,
      );
      if (hexMatch) {
        return this.fromBase({
          r: parseInt(hexMatch[1], 16),
          g: parseInt(hexMatch[2], 16),
          b: parseInt(hexMatch[3], 16),
          a: hexMatch[4] ? parseInt(hexMatch[4], 16) / 255 : 1,
        });
      }

      // Shorthand hex
      const hexShortMatch = trimmed.match(
        /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])?$/i,
      );
      if (hexShortMatch) {
        return this.fromBase({
          r: parseInt(hexShortMatch[1] + hexShortMatch[1], 16),
          g: parseInt(hexShortMatch[2] + hexShortMatch[2], 16),
          b: parseInt(hexShortMatch[3] + hexShortMatch[3], 16),
          a: hexShortMatch[4]
            ? parseInt(hexShortMatch[4] + hexShortMatch[4], 16) / 255
            : 1,
        });
      }

      // rgb()/rgba() string
      const rgbMatch = trimmed.match(
        /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (rgbMatch) {
        return this.fromBase({
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
          a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
        });
      }

      return null;
    }

    // RGB object
    if (
      typeof input === "object" &&
      input !== null &&
      "r" in input &&
      "g" in input &&
      "b" in input
    ) {
      const obj = input as { r: number; g: number; b: number; a?: number };
      return this.fromBase({
        r: obj.r,
        g: obj.g,
        b: obj.b,
        a: obj.a ?? 1,
      });
    }

    // HSL object
    if (
      typeof input === "object" &&
      input !== null &&
      "h" in input &&
      "s" in input &&
      "l" in input
    ) {
      const obj = input as { h: number; s: number; l: number; a?: number };
      const rgb = hslToRgb(obj.h, obj.s, obj.l);
      return this.fromBase({
        ...rgb,
        a: obj.a ?? 1,
      });
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: ColorHslString): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "string") {
      errors.push("HSL string must be a string");
      return { valid: false, errors };
    }

    const str = value.trim();

    if (!HSL_REGEX.test(str) && !HSLA_REGEX.test(str)) {
      errors.push(
        'Invalid format. Use "hsl(h, s%, l%)" or "hsla(h, s%, l%, a)"',
      );
      return { valid: false, errors };
    }

    // Parse and validate values
    try {
      const rgba = this.toBase(value);

      // Values are already clamped in toBase, but we can add warnings
      const hsl = rgbToHsl(rgba.r, rgba.g, rgba.b);
      if (hsl.h < 0 || hsl.h > 360) {
        errors.push("Hue must be between 0 and 360");
      }
      if (hsl.s < 0 || hsl.s > 100) {
        errors.push("Saturation must be between 0 and 100");
      }
      if (hsl.l < 0 || hsl.l > 100) {
        errors.push("Lightness must be between 0 and 100");
      }
      if (rgba.a < 0 || rgba.a > 1) {
        errors.push("Alpha must be between 0 and 1");
      }
    } catch {
      errors.push("Invalid HSL string format");
    }

    return { valid: errors.length === 0, errors };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: ColorHslString, options?: FormatOptions): string {
    const opts = options as ColorFormatOptions | undefined;

    try {
      const rgba = this.toBase(value);
      const hsl = rgbToHsl(rgba.r, rgba.g, rgba.b);
      const a = rgba.a;

      if (opts?.includeAlpha || a < 1) {
        if (opts?.compact) {
          return `hsla(${hsl.h},${hsl.s}%,${hsl.l}%,${a})`;
        }
        return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${a})`;
      }

      if (opts?.compact) {
        return `hsl(${hsl.h},${hsl.s}%,${hsl.l}%)`;
      }
      return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
    } catch {
      return value;
    }
  }
}
